import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { AlertTriangle, Printer, Filter } from 'lucide-react';
import { formatDate } from '../../lib/utils';

export function SyntheseHebdo({
  syntheseData, semaine, synthFilterEmploye, setSynthFilterEmploye, printRef, onPrint
}) {
  if (!syntheseData || !syntheseData.jours?.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          {semaine ? 'Aucune donnee pour cette semaine' : 'Selectionnez une semaine pour voir la synthese'}
        </CardContent>
      </Card>
    );
  }

  const { jours, alertes, employes: synthEmployes, total_heures } = syntheseData;
  const hasAlertes = alertes && alertes.length > 0;
  const filteredSynthEmployes = synthFilterEmploye === 'all' ? synthEmployes : synthEmployes.filter(e => e === synthFilterEmploye);

  const filteredTotalHeures = jours.reduce((total, jour) => {
    return total + jour.creneaux
      .filter(c => synthFilterEmploye === 'all' || c.employe === synthFilterEmploye)
      .reduce((sum, c) => sum + (c.total || 0), 0);
  }, 0);

  return (
    <div className="space-y-6" ref={printRef}>
      {hasAlertes && (
        <Card className="border-amber-400 bg-amber-50/60 dark:bg-amber-950/20" data-testid="coverage-alerts">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Alertes de couverture - {alertes.length} jour(s) avec creneaux non couverts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alertes.map((alerte, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-white dark:bg-background rounded-lg border border-amber-200">
                  <Badge variant="outline" className="border-amber-400 text-amber-700 shrink-0">{alerte.jour}</Badge>
                  <div>
                    <p className="text-sm font-medium">{formatDate(alerte.date)}</p>
                    <p className="text-xs text-muted-foreground">Horaires {alerte.saison} : {alerte.horaires_requis.join(' et ')}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {alerte.creneaux_non_couverts.map((c, j) => (
                        <Badge key={j} variant="destructive" className="text-xs">{c}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Employes planifies</div><div className="text-3xl font-bold mt-1">{filteredSynthEmployes.length}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Total heures{synthFilterEmploye !== 'all' ? ` (${synthFilterEmploye})` : ' semaine'}</div><div className="text-3xl font-bold mt-1">{filteredTotalHeures.toFixed(1)}h</div></CardContent></Card>
        <Card className={hasAlertes ? 'border-amber-400' : 'border-green-400'}>
          <CardContent className="pt-6"><div className="text-sm text-muted-foreground">Couverture</div>
            <div className={`text-3xl font-bold mt-1 ${hasAlertes ? 'text-amber-600' : 'text-green-600'}`}>
              {hasAlertes ? `${alertes.length} alerte(s)` : 'Complete'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle data-testid="synthese-title">Synthese Semaine {semaine}</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={synthFilterEmploye} onValueChange={setSynthFilterEmploye}>
                <SelectTrigger className="w-[180px]" data-testid="synthese-filter-employe">
                  <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous les employes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les employes</SelectItem>
                  {synthEmployes.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={onPrint} className="print:hidden"><Printer className="w-4 h-4 mr-2" /> Imprimer</Button>
            </div>
          </div>
          <CardDescription>
            {jours[0]?.saison === 'ete' ? 'Horaires ete : 10h00 - 22h00' : 'Horaires hiver : 10h00 - 14h30 et 16h00 - 22h00'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-background z-10 min-w-[120px]">Jour</TableHead>
                  {filteredSynthEmployes.map(emp => (<TableHead key={emp} className="text-center min-w-[160px]">{emp}</TableHead>))}
                  <TableHead className="text-center min-w-[80px]">Alerte</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jours.map(jour => (
                  <TableRow key={jour.date} className={jour.has_alertes ? 'bg-amber-50/40 dark:bg-amber-950/10' : ''}>
                    <TableCell className="sticky left-0 bg-background z-10 font-medium">
                      <div>{jour.jour}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(jour.date)}</div>
                    </TableCell>
                    {filteredSynthEmployes.map(emp => {
                      const creneau = jour.creneaux.find(c => c.employe === emp);
                      if (!creneau || creneau.slots.length === 0) return <TableCell key={emp} className="text-center text-muted-foreground text-sm">-</TableCell>;
                      return (
                        <TableCell key={emp} className="text-center">
                          <div className="space-y-1">
                            {creneau.slots.map((s, i) => (
                              <div key={i} className={`text-xs px-2 py-1 rounded ${s.service === 'midi' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'}`}>
                                {s.debut} - {s.fin}
                              </div>
                            ))}
                            <div className="text-xs font-medium text-muted-foreground">{creneau.total.toFixed(1)}h</div>
                          </div>
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-center">
                      {jour.has_alertes ? (
                        <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" />{jour.nb_alertes}</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">OK</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
